// Counter-based maintenance — the fourth tab of the preventive screen.
//
// The flow follows the question people actually ask, in order:
//   "which vehicle?"  -> equipment search
//   "where is it now?" -> counters with current reading, and how many plans hang off each
//   "book today's reading" -> value + the DATE IT WAS TAKEN (a depot round in the morning must
//        not land on the counter curve at 16:00) + who read it + optional delta entry
//   "that was wrong"  -> reverse the document; the history keeps it, struck through
//   "when is it due?" -> per-plan remaining counter + forecast date from the annual estimate
//
// Everything here degrades: an adapter or SAP system without the counter class answers 501 and
// the panel says so instead of showing an empty list that reads as "no counters".
import React, { useRef, useState } from 'react';
import { Search, Gauge, RotateCcw, CalendarClock } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';
import { Card, Button, Row } from './ui';
import {
  searchEquipment, listCounters, listReadings, cancelReading,
  createMeasurementDoc, getPlanCounterStatus, getMeasuringPoint,
} from '../api';

const input = (isDarkMode) =>
  `w-full rounded-xl px-3 py-2 ${isDarkMode ? 'bg-white/10 text-white placeholder-white/40' : 'bg-slate-100 text-slate-800 placeholder-slate-400'}`;

const today = () => new Date().toISOString().slice(0, 10);

/** Numbers with the point's own precision — a km counter with 3 decimals reads as noise. */
const fmt = (v, decimals = 0) =>
  (v == null ? '—' : Number(v).toLocaleString(undefined, {
    minimumFractionDigits: 0, maximumFractionDigits: Math.min(decimals || 0, 3),
  }));

export default function CountersPanel({ me, user, capabilities }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const role = me.profile.id;
  const rights = me.profile.functionRights;
  const supported = !capabilities || capabilities.assetOps !== false;
  const canRead = rights.canConfirm;

  const [query, setQuery] = useState('');
  const [hits, setHits] = useState(null);
  const [counters, setCounters] = useState(null);      // { equipmentId, items }
  const [selected, setSelected] = useState(null);      // point object from the counter list
  const [history, setHistory] = useState(null);        // { documents: [] }
  const [planStatus, setPlanStatus] = useState(null);  // [{ plan, counters }]
  const [form, setForm] = useState({ value: '', date: today(), reader: '', isDifference: false, text: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  // Idempotency keys are minted ONCE, never inside the click handler: a key generated per click
  // makes every retry a new key, so the ledger can never match it and the retry writes a second
  // document (audit finding 19). NewOrderWizard.js:58 is the pattern.
  const readingKey = useRef(crypto.randomUUID());
  // The reversal key belongs to the DOCUMENT, not to the panel: retrying the same reversal must
  // reuse its key, while two different documents must never share one.
  const cancelKeys = useRef(new Map());
  const cancelKeyFor = (docId) => {
    if (!cancelKeys.current.has(docId)) cancelKeys.current.set(docId, crypto.randomUUID());
    return cancelKeys.current.get(docId);
  };

  const sub = isDarkMode ? 'text-white/50' : 'text-slate-500';
  const warn = isDarkMode ? 'text-amber-300' : 'text-amber-700';
  const good = isDarkMode ? 'text-emerald-300' : 'text-emerald-700';

  const fail = (e) => setError(e && e.message ? e.message : String(e));

  const findEquipment = async () => {
    setError(null); setNotice(null); setHits(null); setCounters(null); setSelected(null);
    setHistory(null); setPlanStatus(null);
    try {
      const res = await searchEquipment(user, query, role);
      setHits((res.items || []).slice(0, 8));
    } catch (e) { fail(e); }
  };

  const openCounters = async (equipmentId) => {
    setError(null); setSelected(null); setHistory(null); setPlanStatus(null);
    setCounters({ equipmentId, items: null });
    try {
      const res = await listCounters(user, equipmentId, true, role);
      setCounters({ equipmentId, items: res.items || [] });
    } catch (e) { fail(e); setCounters(null); }
  };

  /** Selecting a counter loads its history AND the due status of every plan on it. */
  const openPoint = async (p) => {
    setError(null); setNotice(null); setSelected(p); setHistory(null); setPlanStatus(null);
    setForm({ value: '', date: today(), reader: '', isDifference: false, text: '' });
    try {
      const h = await listReadings(user, p.point, 20, role);
      setHistory(h);
    } catch (e) { fail(e); }
    try {
      // The counter list only knows HOW MANY plans hang off the point; the due forecast needs
      // the plan ids, which come from the point detail.
      const detail = await getMeasuringPoint(user, p.point, role);
      const rows = [];
      for (const pl of (detail.plans || []).slice(0, 5)) {
        try {
          const s = await getPlanCounterStatus(user, pl.planId, role);
          if (s && (s.counters || []).length) rows.push(s);
        } catch { /* one unreadable plan must not hide the others */ }
      }
      setPlanStatus(rows);
    } catch { /* forecast is a bonus, the history is the point */ }
  };

  const refreshPoint = async () => {
    if (!selected) return;
    if (counters) await openCounters(counters.equipmentId);
    await openPoint(selected);
  };

  const submitReading = async () => {
    setBusy(true); setError(null); setNotice(null);
    try {
      const res = await createMeasurementDoc(user, {
        point: selected.point,
        value: form.value,
        ...(form.date ? { readingDate: form.date } : {}),
        ...(form.reader ? { reader: form.reader } : {}),
        ...(form.isDifference ? { isDifference: true } : {}),
        ...(form.text ? { text: form.text } : {}),
      }, readingKey.current, role);
      // A returned answer — success or business failure — ends this document: the next reading is
      // a different one and gets a fresh key. A THROWN error (timeout/network) deliberately keeps
      // the key: SAP may have booked, and that retry is exactly what idempotency is for.
      readingKey.current = crypto.randomUUID();
      if (res && res.ok === false) setError(res.error || t('pm.cnt.readingFailed'));
      else {
        setNotice(t('pm.cnt.readingSaved', { id: res.measurementDocId }));
        await refreshPoint();
      }
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const reverse = async (docId) => {
    setBusy(true); setError(null); setNotice(null);
    try {
      const res = await cancelReading(user, docId, cancelKeyFor(docId), role);
      if (res && res.ok === false) setError(res.error || t('pm.cnt.cancelFailed'));
      else {
        setNotice(res.alreadyCancelled ? t('pm.cnt.alreadyCancelled') : t('pm.cnt.cancelled', { id: docId }));
        await refreshPoint();
      }
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  if (!supported) {
    return <Card><p className={`text-sm ${warn}`}>{t('pm.cnt.unsupported')}</p></Card>;
  }

  return (
    <div>
      {error && <Card className="mb-3"><p className={isDarkMode ? 'text-rose-300 text-sm' : 'text-rose-600 text-sm'}>{error}</p></Card>}
      {notice && <Card className="mb-3"><p className={`text-sm ${good}`}>{notice}</p></Card>}

      <Card className="mb-3">
        <p className={`flex items-center gap-2 font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
          <Gauge size={18} /> {t('pm.cnt.title')}
        </p>
        <p className={`text-xs mb-2 ${sub}`}>{t('pm.cnt.intro')}</p>
        <div className="flex gap-2 mb-2">
          <input className={input(isDarkMode)} placeholder={t('pm.equip.searchPlaceholder')} value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') findEquipment(); }} />
          <Button variant="secondary" className="!min-h-[44px] !w-auto px-4" onClick={findEquipment}><Search size={18} /></Button>
        </div>
        {hits && hits.map((h) => (
          <button key={h.equipmentId} type="button" className="w-full text-left"
            onClick={() => openCounters(h.equipmentId)}>
            <p className={`text-sm py-1 underline ${sub}`}>{h.equipmentId} — {h.description}</p>
          </button>
        ))}
      </Card>

      {counters && (
        <Card className="mb-3">
          {counters.items === null ? <p className={`text-sm ${sub}`}>{t('pm.common.loading')}</p>
            : counters.items.length === 0 ? <p className={`text-sm ${sub}`}>{t('pm.cnt.noCounters')}</p>
              : (
                <div className="space-y-2">
                  {counters.items.map((p) => (
                    <button key={p.point} type="button"
                      className={`w-full text-left rounded-xl px-3 py-2 ${selected && selected.point === p.point
                        ? (isDarkMode ? 'bg-white/15' : 'bg-slate-200')
                        : (isDarkMode ? 'bg-white/5 hover:bg-white/10' : 'bg-slate-100 hover:bg-slate-150')}`}
                      onClick={() => openPoint(p)}>
                      <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {p.description || `${t('pm.cnt.point')} ${p.point}`}
                      </p>
                      <p className={`text-xs ${sub}`}>
                        {fmt(p.currentReading, p.decimals)} {p.unit}
                        {p.currentReadingDate ? ` · ${p.currentReadingDate}` : ''}
                        {p.annualEstimate ? ` · ${t('pm.cnt.perYear', { v: fmt(p.annualEstimate, 0), u: p.unit })}` : ''}
                        {p.planCount ? ` · ${t('pm.cnt.planCount', { n: p.planCount })}` : ` · ${t('pm.cnt.noPlans')}`}
                      </p>
                    </button>
                  ))}
                </div>
              )}
        </Card>
      )}

      {selected && (
        <>
          {canRead && (
            <Card className="mb-3">
              <p className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                {t('pm.cnt.recordTitle', { unit: selected.unit || '' })}
              </p>
              <div className="space-y-2">
                <input className={input(isDarkMode)} inputMode="decimal"
                  placeholder={form.isDifference ? t('pm.cnt.deltaValue') : t('pm.cnt.totalValue')}
                  value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
                <label className={`flex items-center gap-2 text-sm ${sub}`}>
                  <input type="checkbox" checked={form.isDifference}
                    onChange={(e) => setForm({ ...form, isDifference: e.target.checked })} />
                  {t('pm.cnt.isDifference')}
                </label>
                <div className="flex gap-2">
                  <input className={input(isDarkMode)} type="date" max={today()} value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })} />
                  <input className={input(isDarkMode)} placeholder={t('pm.cnt.reader')} value={form.reader}
                    onChange={(e) => setForm({ ...form, reader: e.target.value })} />
                </div>
                <p className={`text-xs ${sub}`}>{t('pm.cnt.dateHint')}</p>
                <input className={input(isDarkMode)} placeholder={t('pm.cnt.readingText')} value={form.text}
                  onChange={(e) => setForm({ ...form, text: e.target.value })} />
                <Button variant="confirm" className="!min-h-[48px]" disabled={busy || !form.value}
                  onClick={submitReading}>
                  {t('pm.equip.recordReading')}
                </Button>
              </div>
            </Card>
          )}

          {planStatus && planStatus.length > 0 && (
            <Card className="mb-3">
              <p className={`flex items-center gap-2 font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                <CalendarClock size={18} /> {t('pm.cnt.dueTitle')}
              </p>
              <div className="space-y-3">
                {planStatus.map((s) => (
                  <div key={s.planId}>
                    <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {s.planId} — {s.description}
                    </p>
                    {(s.counters || []).map((c) => (
                      <div key={`${s.planId}-${c.cycleNo}`} className="text-sm">
                        <Row label={t('pm.cnt.cycle')} value={`${fmt(c.cycle, 0)} ${c.unit}${c.cycleText ? ` (${c.cycleText})` : ''}`} />
                        <Row label={t('pm.cnt.nextDueReading')} value={`${fmt(c.nextDueReading, 0)} ${c.unit}`} />
                        <Row label={t('pm.cnt.remaining')} value={`${fmt(c.remaining, 0)} ${c.unit}`} />
                        {c.estimatedDueDate && <Row label={t('pm.cnt.forecast')} value={c.estimatedDueDate} />}
                        {s.nextDueDateSap && <Row label={t('pm.cnt.sapScheduled')} value={s.nextDueDateSap} />}
                      </div>
                    ))}
                    {!s.nextDueDateSap && (
                      <p className={`text-xs ${warn}`}>{t('pm.cnt.notScheduledYet')}</p>
                    )}
                  </div>
                ))}
              </div>
              <p className={`text-xs mt-2 ${sub}`}>{t('pm.cnt.forecastHint')}</p>
            </Card>
          )}

          <Card>
            <p className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              {t('pm.cnt.historyTitle')}
            </p>
            {!history ? <p className={`text-sm ${sub}`}>{t('pm.common.loading')}</p>
              : (history.documents || []).length === 0 ? <p className={`text-sm ${sub}`}>{t('pm.cnt.noReadings')}</p>
                : (
                  <div className="space-y-1">
                    {history.documents.map((d) => (
                      <div key={d.measurementDocId} className="flex items-center justify-between gap-2">
                        <p className={`text-sm ${d.cancelled ? `line-through ${sub}` : (isDarkMode ? 'text-white/80' : 'text-slate-700')}`}>
                          {(d.recordedAt || '').replace('T', ' ').slice(0, 16)} · {fmt(d.value, selected.decimals)} {d.valueUnit}
                          {d.difference ? ` (+${fmt(d.difference, selected.decimals)})` : ''}
                          {d.reader ? ` · ${d.reader}` : ''}
                          {!d.manual ? ` · ${t('pm.cnt.generated')}` : ''}
                          {d.text ? ` · ${d.text}` : ''}
                        </p>
                        {canRead && !d.cancelled && (
                          <button type="button" disabled={busy} title={t('pm.cnt.cancel')}
                            className={`shrink-0 rounded-lg px-2 py-1 ${isDarkMode ? 'text-white/60 hover:text-white' : 'text-slate-500 hover:text-slate-800'}`}
                            onClick={() => reverse(d.measurementDocId)}>
                            <RotateCcw size={16} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
            <p className={`text-xs mt-2 ${sub}`}>{t('pm.cnt.historyHint')}</p>
          </Card>
        </>
      )}
    </div>
  );
}
